






























import { Vue, Component, Watch, Prop } from 'vue-property-decorator';

import CloseModalIcon from '@/components/icons/CloseModal.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import NavButtonItem from '@/componentsV2/molecules/NavButtonItem/NavButtonItem.vue';

import eventBus from '@/utils/eventBus';

import LocationsService from '@/api/LocationsService';
import { ILocation } from '@/models/location';
import { IOfferListItem } from '@/models/offer';

// Step components
import AddLocation, { LocationFormModalMethod } from './Steps/AddLocation.vue';
import AddOffer from './Steps/AddOffer.vue';
import PlanOverview from './Steps/PlanOverview.vue';
import { EBillingStatusTypes } from '@/store/modules/businessTypes';
import { getBillingIsHidden } from '@/utils/billing';
import segmentIo from '@/utils/segment.io';

export type LocationFormModalStep = {
  slug: string;
  label: string;
};

export enum ELocationFormModalStepSlug {
  ADD_LOCATION = 'add-location',
  ADD_OFFER = 'add-offer',
  PLAN_OVERVIEW = 'plan-overview'
}

@Component({
  components: {
    Typography,
    CloseModalIcon,
    FilledButton,
    NavButtonItem,
    AddLocation,
    AddOffer,
    PlanOverview
  }
})
export default class LocationFormModal extends Vue {
  @Prop() method!: LocationFormModalMethod;
  @Prop() offers?: IOfferListItem[];
  @Prop() location?: ILocation;
  @Prop() reloadLocations!: () => void;
  @Prop({ default: () => ELocationFormModalStepSlug.ADD_LOCATION })
  initialStep!: ELocationFormModalStepSlug;

  ELocationFormModalStepSlug = ELocationFormModalStepSlug;

  activeStepSlug: string = this.initialStep;
  newLocationId: string = '';

  locationService = new LocationsService({ disableToastMessage: true });

  subscription = {};

  get hasSteps() {
    return (
      this.componentByStepSlug[this.activeStepSlug].showNav &&
      this.method === 'create' &&
      (this.hasOffers || this.subscribedBilling)
    );
  }

  get hasOffers() {
    return this.offers && this.offers.length;
  }

  // The only thing this getter drives here is the "Plan overview" step — both in
  // the nav and in `steps` — so hiding billing drops that step from the wizard and
  // the location is created exactly as it is today (TWISM-19011). It has to stay in
  // lockstep with the same getter in `Steps/AddLocation.vue`, which decides whether
  // to emit `goToNextStep` at all.
  get subscribedBilling() {
    if (getBillingIsHidden()) return false;

    return this.billingStatus === EBillingStatusTypes.SUBSCRIBED;
  }

  get billingStatus() {
    return this.business.subscriptionStatus;
  }

  get business() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get steps(): LocationFormModalStep[] {
    const steps = [
      {
        slug: ELocationFormModalStepSlug.ADD_LOCATION,
        label: 'Add Location'
      }
    ];

    if (this.subscribedBilling)
      steps.push({
        slug: ELocationFormModalStepSlug.PLAN_OVERVIEW,
        label: 'Plan overview'
      });

    if (this.hasOffers)
      steps.push({
        slug: ELocationFormModalStepSlug.ADD_OFFER,
        label: 'Add Offer'
      });

    return steps;
  }

  get componentByStepSlug(): {
    [k: string]: {
      showNav: boolean;
      component: string;
    };
  } {
    return {
      [ELocationFormModalStepSlug.ADD_LOCATION]: {
        component: 'AddLocation',
        showNav: true
      },
      [ELocationFormModalStepSlug.PLAN_OVERVIEW]: {
        component: 'PlanOverview',
        showNav: true
      },
      [ELocationFormModalStepSlug.ADD_OFFER]: {
        component: 'AddOffer',
        showNav: true
      }
    };
  }

  created() {
    document.onkeydown = this.handleOnkeydown;
    this.resizeEventHandler();
    segmentIo.modal('Add Location Form');
  }

  @Watch('$route')
  onRouteChange() {
    this.onClose();
  }

  isActiveStep(stepSlug: string) {
    return this.activeStepSlug === stepSlug;
  }

  handleGoToNextStep({ locationId, subscription }: any = {}) {
    const indexOfStep = this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug
    );

    this.activeStepSlug = this.steps[indexOfStep + 1].slug;

    if (subscription) this.subscription = subscription;

    if (locationId) this.newLocationId = locationId;
  }

  onClose() {
    eventBus.$emit('closeModal');
  }

  handleOnkeydown({ key }: KeyboardEvent) {
    if (key === 'Escape') {
      this.onClose();
    }
  }

  resizeEventHandler() {
    const vh = window.innerHeight * 0.01;

    document.documentElement.style.setProperty(
      '--location-form-modal-vh',
      `${vh}px`
    );
  }

  percentageConcluded(step: LocationFormModalStep) {
    const indexOfStep = this.steps.findIndex(
      refStep => refStep.slug === step.slug
    );

    const currentIndex = this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug.split(':')[0]
    );

    const isPrevLink = indexOfStep < currentIndex;

    return this.isActiveStep(step.slug) || isPrevLink ? 100 : 0;
  }
}
